import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.tsx';
import { useCallback } from 'react';
import { usePersistedCardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView.ts';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { Button, Flex, SimpleGrid, Skeleton, Tooltip } from '@mantine/core';
import { CardBodySkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/CardBodySkeleton.tsx';
import { useNavigate } from 'react-router-dom';
import { InvestmentDocumentCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsContent/components/InvestmentDocumentCard/InvestmentDocumentCard.tsx';
import { buildDocumentCardProps } from './InvestmentLegalDocuments.utils.tsx';
import { InvestmentLegalDocumentsTable } from './InvestmentLegalDocumentsTable.tsx';
import { useInvestmentDetailsLegalDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsLegalDocuments.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	documentsForInvestmentPath,
	mipManagePath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';

interface Props {
	onManageRequirements: () => void;
}

export const InvestmentLegalDocuments = ({ onManageRequirements }: Props) => {
	const tMipManage = useTranslationMipManage();
	const [activeView, setActiveView] = usePersistedCardTableView(
		StorageKeys.MIP_INVESTMENT_DETAILS_DOCUMENTS_VIEW,
		'cards'
	);
	const { data, isLoading } = useInvestmentDetailsLegalDocuments();
	const legalDocuments = data?.legalDocuments;
	const canMutateLegalDocuments = !!data?.canMutateLegalDocuments;
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const navigate = useNavigate();

	const navigateToDocumentDetails = useCallback(
		(documentWorkflowInstanceId: string) => {
			navigate(
				mipManagePath(MIP_MANAGE_ROUTES.DOCUMENT_DETAILS, {
					dealId,
					mipId,
					documentWorkflowInstanceId,
				})
			);
		},
		[navigate, dealId, mipId]
	);

	const handleReview = useCallback(() => {
		if (!legalDocuments?.length) {
			return;
		}
		const [onlyDocument] = legalDocuments;
		if (legalDocuments.length === 1 && onlyDocument) {
			navigateToDocumentDetails(onlyDocument.id);
			return;
		}
		navigate(documentsForInvestmentPath({ dealId, mipId, investmentWorkflowId: investmentId }));
	}, [legalDocuments, navigateToDocumentDetails, navigate, dealId, mipId, investmentId]);

	const renderContent = () => {
		if (isLoading) {
			if (activeView === 'cards') {
				return (
					<SimpleGrid cols={2} spacing="0.5rem" p="0.5rem">
						<Skeleton height={72} radius="sm" />
						<Skeleton height={72} radius="sm" />
						<Skeleton height={72} radius="sm" />
						<Skeleton height={72} radius="sm" />
					</SimpleGrid>
				);
			}
			return <CardBodySkeleton count={3} rowHeight={48} mih={168} />;
		}

		if (!legalDocuments?.length) {
			return (
				<InvestmentPlaceholder
					icon="folder"
					label={tMipManage('investment_legal_documents.placeholder_label')}
					mih={168}
				/>
			);
		}

		if (activeView === 'cards') {
			return (
				<SimpleGrid cols={2} spacing="0.5rem" p="0.5rem">
					{legalDocuments.map((document) => (
						<InvestmentDocumentCard
							key={document.id}
							{...buildDocumentCardProps({
								document,
								tMipManage,
								onUnassignedSignerClick: navigateToDocumentDetails,
							})}
						/>
					))}
				</SimpleGrid>
			);
		}

		return (
			<InvestmentLegalDocumentsTable
				legalDocuments={legalDocuments}
				onUnassignedSignerClick={navigateToDocumentDetails}
			/>
		);
	};

	const manageButton = (
		<Button
			variant="default"
			onClick={onManageRequirements}
			disabled={!canMutateLegalDocuments}
		>
			{tMipManage('investment_legal_documents.button.manage_requirements')}
		</Button>
	);

	return (
		<InvestmentDetailsCard
			cardLabel={tMipManage('investment_legal_documents.card_label')}
			actions={
				<Flex gap="0.5rem" align="center">
					<Button
						variant="default"
						onClick={handleReview}
						disabled={!legalDocuments?.length}
					>
						{tMipManage('investment_legal_documents.button.review')}
					</Button>

					{canMutateLegalDocuments ? (
						manageButton
					) : (
						<Tooltip label={tMipManage('investment_legal_documents.frozen_tooltip')}>
							<div>{manageButton}</div>
						</Tooltip>
					)}

					<CardTableViewSelector value={activeView} onViewChange={setActiveView} />
				</Flex>
			}
		>
			{renderContent()}
		</InvestmentDetailsCard>
	);
};
